import React, { useEffect, useState } from 'react';

import { Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconEdit,
  IconRotateClockwise2,
  IconTrash,
  IconArrowBackUp,
  IconHandStop,
  IconPencil,
} from '@tabler/icons-react';
import CreditsProgressBar from 'components/CreditsProgressBar';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreditDispensations from 'hooks/creditDispensation/useCreditDispensations';
import { CellProps } from 'react-table';
import { CreditDispensationListView } from 'types';

import DrawerAthletePackageDetailView from '../../Packages/components/AthletePackageDetail/AthletePackageDetail';
import ModalCreditDispensationDelete from '../../Packages/components/AthletePackageDetail/components/CreditsTab/components/ModalCreditDispensationDelete';
import ModalCreditDispensationEdit from '../../Packages/components/AthletePackageDetail/components/CreditsTab/components/ModalCreditDispensationEdit';
import ModalCreditDispensationUndo from '../../Packages/components/AthletePackageDetail/components/CreditsTab/components/ModalCreditDispensationUndo';

interface IAthleteCreditsTableContainerProps {
  athleteId: string;
  validityDate: Date | null;
  permission?: boolean;
}

const AthleteCreditsTableContainer = ({
  athleteId,
  validityDate,
  permission = true,
}: IAthleteCreditsTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const { data, isSuccess, isError, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useCreditDispensations({
      athleteId: athleteId,
      validityDate: validityDate ? dayjs(validityDate).format('YYYY-MM-DD') : undefined,
    });
  const [isEditModalOpen, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isUndoModalOpen, { open: openUndoModal, close: closeUndoModal }] = useDisclosure(false);

  const [selectedCD, setSelectedCD] = useState<CreditDispensationListView | null>(null);
  const [tableData, setTableData] = useState<CreditDispensationListView[]>([]);
  const [isViewFormOpened, { open: openViewForm, close: closeViewForm }] = useDisclosure(false);
  const [athletePackageId, setAthletePackageId] = useState<string | undefined>();
  const handleEditClick = (creditDispensation: CreditDispensationListView): void => {
    setSelectedCD(creditDispensation);
    openEditModal();
  };
  const handleDeleteClick = (creditDispensation: CreditDispensationListView): void => {
    setSelectedCD(creditDispensation);
    openDeleteModal();
  };
  const handleUndoClick = (creditDispensation: CreditDispensationListView): void => {
    setSelectedCD(creditDispensation);
    openUndoModal();
  };
  const handleCloseAthletePackageViewForm = (): void => {
    closeViewForm();
    setAthletePackageId(undefined);
  };
  const handleViewPackage = (rowId: string): void => {
    setAthletePackageId(rowId);
    openViewForm();
  };

  const ManualEditIcon = (): JSX.Element => (
    <div style={{ position: 'relative', width: 18, height: 18 }}>
      <IconHandStop size={18} style={{ position: 'absolute', color: 'gray', left: 0, top: 0 }} />
      <IconPencil size={12} style={{ position: 'absolute', color: 'gray', right: 1, bottom: -2 }} />
    </div>
  );

  const getMenuItems = (creditDispensation: CreditDispensationListView): any => {
    const isRollover = creditDispensation.isRollover;
    const hasUsedCredits = creditDispensation.creditsUsed > 0;

    const items = [
      {
        title: 'Edit',
        onClick: () => handleEditClick(creditDispensation),
        icon: <IconEdit size={theme.fontSizes.sm} />,
        disabled: creditDispensation.hasRolledOver,
      },
      {
        title: 'Delete',
        color: 'red',
        onClick: () => handleDeleteClick(creditDispensation),
        disabled: creditDispensation.hasRolledOver,
        icon: <IconTrash size={theme.fontSizes.sm} />,
      },
    ];

    if (isRollover) {
      items.push({
        title: 'Undo',
        onClick: () => handleUndoClick(creditDispensation),
        // disable if any credits have been used on the rollover
        disabled: hasUsedCredits,
        icon: <IconArrowBackUp size={theme.fontSizes.sm} />,
      });
    }
    return items;
  };

  useEffect(() => {
    if (data) {
      const newData = data.pages.map(page => page.data.results).flat();
      setTableData(newData);
    }
  }, [data, validityDate, setTableData]);

  const columns = [
    {
      Header: 'Package',
      accessor: 'packageName',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <Text
          weight={500}
          onClick={() => handleViewPackage(row.original.athletePackage ? row.original.athletePackage : '')}
          td='underline'
          fw='bold'
          sx={{
            '&:hover': {
              cursor: permission ? 'pointer' : 'default',
            },
          }}
        >
          {row.original.packageName}
        </Text>
      ),
    },
    {
      Header: 'Event Type',
      accessor: 'name',
    },
    {
      Header: 'Validity Period',
      accessor: 'dispenseDate',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <Text>
          {dayjs(row.original.dispenseDate).format(dateFormat)} -{' '}
          {row.original.isRollover == false && // Show package's end date if not a rollover and package's end date is before expiration date
          row.original.athletePackageCancellationDate && // Clearer version of the code can be seen in CreditDispensationRow.tsx
          row.original.expirationDate && // Couldn't find a better way to do this inside the cell
          dayjs(row.original.athletePackageCancellationDate) < dayjs(row.original.expirationDate)
            ? dayjs(row.original.athletePackageCancellationDate).format(dateFormat) + ' - Early Cancellation'
            : row.original.expirationDate
            ? dayjs(row.original.expirationDate).format(dateFormat)
            : 'None'}
        </Text>
      ),
    },
    {
      Header: 'Credit Usage',
      accessor: 'creditUsage',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <CreditsProgressBar
          key={row.original.creditDispensationId}
          totalCredits={row.original.credits}
          creditsUsed={row.original.creditsUsed}
        />
      ),
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <Group spacing={4} position='left' px='xs'>
          {row.original.isRollover && <IconRotateClockwise2 style={{ color: 'black' }} />}
          {row.original.manualEdit && <ManualEditIcon />}
        </Group>
      ),
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <Group position='right' px='xs'>
          <Menu disabled={!permission} items={getMenuItems(row.original)} />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isError={isError}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
      <ModalCreditDispensationEdit
        opened={isEditModalOpen}
        handleClose={closeEditModal}
        creditDispensation={selectedCD}
      />
      <ModalCreditDispensationDelete
        opened={isDeleteModalOpen}
        handleClose={closeDeleteModal}
        creditDispensation={selectedCD}
      />
      <ModalCreditDispensationUndo
        opened={isUndoModalOpen}
        handleClose={closeUndoModal}
        creditDispensation={selectedCD}
      />
      <DrawerAthletePackageDetailView
        athletePackageId={athletePackageId}
        setAthletePackageId={setAthletePackageId}
        opened={isViewFormOpened}
        closeHandler={handleCloseAthletePackageViewForm}
        permission={permission}
      />
    </>
  );
};

export default AthleteCreditsTableContainer;
